/**
 * "Why am I waiting?": one honest answer from what the browser knows, for the waiting card and the banners
 * (components/sandbox/SandboxWaking.jsx, fed by hooks/useSandboxWake.js). Pure: no React, no fetch, no clock.
 *
 * Inputs: whether the browser is online (navigator.onLine + online/offline events), the last answer of the sandbox
 * gateway's `GET /api/v1/sandbox/state`, the last failure of that call (HTTP status, or none when nothing answered,
 * and its message), how long the last answer took, how many tries failed in a row, whether a new Flow version was
 * detected (utils/staleBuild.js, or `flow.version` changing), and the wake's elapsed time counted on locally.
 *
 * The answer's kinds, browser-side first, then server-side, so the two are never mixed up:
 * - browser: `offline` (no network), `unreachable` (online, but nothing answers: Flow may be restarting), `slow`
 *   (answers take more than SLOW_ANSWER_MS);
 * - server: `flow_updating` (a release rolls out: `flow.updating`, phase `flow_updating`, or 502/503/504 / envoy's
 *   "upstream connect error"), `flow_updated` (a new version is out: reload), `blocked` (the sandbox can't start: hours
 *   or credits used up, removed from the organization…; with the next step), `waking` (the sandbox starts: the step in
 *   plain words, elapsed vs usual).
 * `null`: nothing to wait for (running, asleep, no answer yet).
 *
 * The state answer, new contract: `{ state, wakePhase?, phase: ready|asleep|waking|blocked|flow_updating, step?,
 * reason?, nextStep?, startedAt, expectedSeconds, elapsedSeconds, remainingSeconds, youNeedToAct, message, flow }`.
 * Older gateways answer `{ state, phase: checking|creating|resuming|starting|network, elapsedMs?, error?, code? }`:
 * normalizeSandboxState() reads both.
 */

export const SLOW_ANSWER_MS = 5000;
/** Signals older than this (a slow answer, a failure) no longer count. */
export const SIGNAL_TTL_MS = 60_000;
/** After this many failed tries in a row: "taking longer than usual". */
export const LONGER_AFTER_TRIES = 12;
/** A wake without the server's estimate: Retry after this long (the legacy card's 45 s). */
export const SLOW_AFTER_MS = 45_000;

export const NO_ACTION_HINT = 'You don\'t need to do anything — it continues by itself.';

const LEGACY_PHASES = new Set(['checking', 'creating', 'resuming', 'starting', 'network']);
const NEW_PHASES = new Set(['ready', 'asleep', 'waking', 'blocked', 'flow_updating']);
const FLOW_DOWN_STATUSES = new Set([502, 503, 504]);
const AUTH_STATUSES = new Set([401, 403]);
// Envoy in front of a pod that restarts answers these (with a 503 or none at all).
const UPSTREAM_DOWN = /upstream connect error|disconnect\/reset|connection (timeout|refused|termination)|no healthy upstream/i;
// What browsers say when nothing answered at all.
const NO_ANSWER = /could not reach|failed to fetch|networkerror|load failed/i;

/** The retry delay after `tries` failed tries in a row: 4, 6, 8, 10 s, then every 10 s (vfs-service admin's). */
export function retryDelayMs(tries) {
  const steps = [4, 6, 8, 10];
  return steps[Math.min(Math.max(tries, 1), steps.length) - 1] * 1000;
}

/**
 * What a failed request says about Flow: `flow_down` (502/503/504 or envoy's upstream messages: Flow restarts),
 * `no_answer` (nothing answered, status 0 or the browser's network error: offline, or Flow unreachable), `auth`
 * (signed out: not an outage), or null (Flow answered: 404, 500…).
 */
export function classifyFailure(failure) {
  if (!failure) { return null; }
  const status = Number(failure.status) || null;
  const message = String(failure.message || '');
  if (AUTH_STATUSES.has(status)) { return 'auth'; }
  if (FLOW_DOWN_STATUSES.has(status) || UPSTREAM_DOWN.test(message)) { return 'flow_down'; }
  if (failure.status === 0 || NO_ANSWER.test(message)) { return 'no_answer'; }
  return null;
}

/** vfs-service admin's "Flow is being updated" test: any failure that is not Flow answering (and not auth). */
export const isFlowUpdatingFailure = (failure) => ['flow_down', 'no_answer'].includes(classifyFailure(failure));

const secondsOrNull = (value) => (typeof value === 'number' && Number.isFinite(value) ? Math.max(0, value) : null);

/** Phase for an answer without the new `phase` (or with the old vocabulary in it). */
function legacyPhase(state) {
  if (state === 'running') { return 'ready'; }
  if (state === 'waking') { return 'waking'; }
  if (state === 'failed') { return 'blocked'; }
  if (state === 'asleep') { return 'asleep'; }
  return null;
}

/** The state answer in one shape, from the new contract or an older gateway's. null for no answer. */
export function normalizeSandboxState(answer) {
  if (!answer || typeof answer !== 'object') { return null; }
  const rawPhase = answer.phase;
  const legacy = !NEW_PHASES.has(rawPhase);
  const wakePhase = answer.wakePhase || (LEGACY_PHASES.has(rawPhase) ? rawPhase : null);
  const phase = legacy ? legacyPhase(answer.state) : rawPhase;
  const elapsedSeconds = secondsOrNull(answer.elapsedSeconds)
    ?? (typeof answer.elapsedMs === 'number' ? Math.max(0, Math.round(answer.elapsedMs / 1000)) : null);
  const flow = answer.flow && typeof answer.flow === 'object' ? answer.flow : {};
  return {
    state: answer.state || null,
    phase,
    wakePhase,
    step: answer.step || null,
    reason: answer.reason || answer.code || null,
    status: answer.status || null,
    nextStep: answer.nextStep || null,
    expectedSeconds: secondsOrNull(answer.expectedSeconds),
    elapsedSeconds,
    remainingSeconds: secondsOrNull(answer.remainingSeconds),
    youNeedToAct: typeof answer.youNeedToAct === 'boolean' ? answer.youNeedToAct : null,
    message: answer.message || answer.error || null,
    flow: {
      updating: flow.updating === true,
      reason: flow.reason || null,
      version: flow.version || null,
      expectedSeconds: secondsOrNull(flow.expectedSeconds),
    },
    legacy,
  };
}

export function formatElapsed(seconds) {
  const s = Math.max(0, Math.round(seconds));
  return s < 60 ? `${s} s` : `${Math.floor(s / 60)} min ${String(s % 60).padStart(2, '0')} s`;
}

/** "about 40 s", "about 2 min". */
export function formatAbout(seconds) {
  const s = Math.max(0, Math.round(seconds));
  return s < 90 ? `about ${s} s` : `about ${Math.round(s / 60)} min`;
}

const minutesText = (seconds) => {
  const minutes = Math.max(1, Math.ceil(seconds / 60));
  return `${minutes} minute${minutes === 1 ? '' : 's'}`;
};

export const STEP_TEXT = {
  scheduling: 'Finding a machine for your sandbox',
  pulling_image: 'Downloading the new version of your sandbox (first start after a Flow update)',
  starting: 'Starting your sandbox',
  almost_ready: 'Almost ready: connecting to it',
};

// Older gateways: the wake's phase, with the share of the bar it covers.
export const LEGACY_PHASE_TEXT = {
  checking: { text: 'Checking your sandbox', from: 3, to: 10 },
  creating: { text: 'Creating your sandbox (the first time only)', from: 10, to: 25 },
  resuming: { text: 'Waking it up', from: 10, to: 25 },
  starting: { text: 'Starting it: getting a machine and booting', from: 25, to: 85 },
  network: { text: 'Almost there: connecting to it', from: 88, to: 96 },
};
const LEGACY_TYPICAL_S = 180;

const BLOCKED_TITLES = {
  hours_exhausted: 'Your plan\'s hours are used up',
  hours_grace: 'Your plan\'s hours are used up',
  credits_exhausted: 'Your credits are used up',
  removed_from_organization: 'You are no longer in this organization',
  merged_into_other_account: 'This account was merged into another one',
  deleting: 'Your sandbox is being deleted',
  restoring: 'Your sandbox is being restored',
  migrating: 'Your sandbox is moving to another machine',
  cluster_quota: 'There is no room for your sandbox right now',
  limit: 'Your plan\'s limit is reached',
};
const PLAN_REASONS = new Set(['hours_exhausted', 'hours_grace', 'credits_exhausted', 'limit']);
const RETRY_REASONS = new Set(['start_failed', 'not_ready']);

function wakingState(sandbox, elapsedSeconds) {
  const elapsed = elapsedSeconds ?? sandbox.elapsedSeconds ?? 0;
  const expected = sandbox.expectedSeconds;
  const stepText = STEP_TEXT[sandbox.step] || (LEGACY_PHASE_TEXT[sandbox.wakePhase] || LEGACY_PHASE_TEXT.checking).text;
  let timing;
  let percent;
  let slower = false;
  if (expected) {
    slower = elapsed > expected;
    timing = `${formatElapsed(elapsed)} · usually ${formatAbout(expected)}${slower ? ' · taking longer than usual' : ''}`;
    percent = Math.round(Math.min(95, 5 + (90 * elapsed) / expected));
  } else {
    const phase = LEGACY_PHASE_TEXT[sandbox.wakePhase] || LEGACY_PHASE_TEXT.checking;
    const span = Math.min(1, elapsed / LEGACY_TYPICAL_S);
    timing = formatElapsed(elapsed);
    percent = Math.round(phase.from + ((phase.to - phase.from) * span));
  }
  const youNeedToAct = sandbox.youNeedToAct === true;
  return {
    kind: 'waking',
    side: 'server',
    title: 'Waking your sandbox…',
    detail: `${stepText} · ${timing}`,
    // Without the server's estimate: the old promise of "up to 3 minutes".
    note: expected ? null : 'You may expect up to 3 minutes for it to be ready.',
    elapsedSeconds: elapsed,
    expectedSeconds: expected,
    slower,
    percent,
    youNeedToAct,
    nextStep: youNeedToAct ? sandbox.nextStep : null,
    hint: youNeedToAct ? null : NO_ACTION_HINT,
    action: slower || (!expected && elapsed * 1000 >= SLOW_AFTER_MS) ? 'retry' : null,
  };
}

function blockedState(sandbox) {
  const reason = sandbox.reason;
  const plan = PLAN_REASONS.has(reason) || sandbox.status === 429;
  // Older gateways had no `youNeedToAct`: a failed start was the person's to retry.
  const youNeedToAct = sandbox.youNeedToAct ?? true;
  let action = null;
  if (plan) { action = 'plan'; } else if (youNeedToAct && (RETRY_REASONS.has(reason) || !BLOCKED_TITLES[reason])) { action = 'retry'; }
  const message = sandbox.message || 'It did not answer in time.';
  return {
    kind: 'blocked',
    side: 'server',
    title: BLOCKED_TITLES[reason] || 'Your sandbox could not start',
    detail: sandbox.legacy ? `${message} Your projects are safe.` : message,
    reason,
    youNeedToAct,
    nextStep: youNeedToAct ? sandbox.nextStep : null,
    hint: youNeedToAct ? null : NO_ACTION_HINT,
    action,
  };
}

function updatingState({ sandbox, failureKind, tries }) {
  const fromPhase = sandbox?.phase === 'flow_updating' ? sandbox.remainingSeconds : null;
  const seconds = fromPhase ?? sandbox?.flow.expectedSeconds ?? null;
  const longer = failureKind && tries >= LONGER_AFTER_TRIES;
  const back = seconds ? `Back in about ${minutesText(seconds)}.` : 'Back in a few minutes.';
  return {
    kind: 'flow_updating',
    side: 'server',
    title: 'Flow is being updated',
    detail: longer ? `${back} This is taking longer than usual; still retrying.` : back,
    minutes: seconds ? Math.max(1, Math.ceil(seconds / 60)) : null,
    reason: sandbox?.flow.reason || null,
    youNeedToAct: false,
    nextStep: null,
    hint: NO_ACTION_HINT,
    action: null,
  };
}

/**
 * The one answer to "why am I waiting?".
 * @param {object} input
 * @param {boolean} [input.online] navigator.onLine
 * @param {object} [input.status] the last state answer (raw, either contract)
 * @param {object} [input.failure] the last failure: `{ status, message, at }` (status 0/none: nothing answered)
 * @param {number} [input.tries] failed tries in a row
 * @param {number} [input.latencyMs] how long the last answer took (or the one in flight so far)
 * @param {number} [input.latencyAt] when that was measured
 * @param {boolean} [input.newVersion] a new Flow version was detected
 * @param {number} [input.elapsedSeconds] the wake's elapsed time, counted on locally between answers
 * @param {number} [input.now] the clock
 */
export function waitingState({
  online = true, status = null, failure = null, tries = 0, latencyMs = 0, latencyAt = 0, newVersion = false,
  elapsedSeconds = null, now = 0,
} = {}) {
  if (!online) {
    return {
      kind: 'offline',
      side: 'browser',
      title: 'Your connection seems offline',
      detail: 'Flow can\'t be reached from this device right now.',
      youNeedToAct: true,
      nextStep: 'Check your Wi-Fi or network. Flow continues by itself once you are back online.',
      hint: null,
      action: null,
    };
  }
  const sandbox = normalizeSandboxState(status);
  const fresh = (at) => !at || !now || now - at < SIGNAL_TTL_MS;
  const failureKind = failure && fresh(failure.at) ? classifyFailure(failure) : null;

  if (failureKind === 'no_answer') {
    return {
      kind: 'unreachable',
      side: 'browser',
      title: 'We can\'t reach Flow',
      detail: 'If your connection works, Flow may be restarting for an update — retrying by itself.',
      longer: tries >= LONGER_AFTER_TRIES,
      youNeedToAct: false,
      nextStep: null,
      hint: tries >= LONGER_AFTER_TRIES ? 'This is taking longer than usual. Check your connection, or try again later.' : NO_ACTION_HINT,
      action: null,
    };
  }
  if (failureKind === 'flow_down' || sandbox?.flow.updating || sandbox?.phase === 'flow_updating') {
    return updatingState({ sandbox, failureKind, tries });
  }
  if (sandbox?.phase === 'blocked') { return blockedState(sandbox); }
  if (sandbox?.phase === 'waking') { return wakingState(sandbox, elapsedSeconds); }
  if (newVersion) {
    return {
      kind: 'flow_updated',
      side: 'server',
      title: 'Flow was updated',
      detail: 'A new version is out. Reload to get it; your work is saved.',
      youNeedToAct: true,
      nextStep: 'Reload the page.',
      hint: null,
      action: 'reload',
    };
  }
  if (latencyMs >= SLOW_ANSWER_MS && fresh(latencyAt)) {
    return {
      kind: 'slow',
      side: 'browser',
      title: 'Your connection seems slow',
      detail: `Flow took ${formatElapsed(latencyMs / 1000)} to answer. It is still loading…`,
      youNeedToAct: false,
      nextStep: null,
      hint: NO_ACTION_HINT,
      action: null,
    };
  }
  return null;
}
